import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiRequest, withApi } from "../utils/api";

const AUTH_TOKEN_KEY = "ashara_admin_token";
const AUTH_EMPLOYEE_KEY = "ashara_admin_employee";

const parseJsonSafe = (value) => {
    if (!value) return null;
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};

// En disco solo se guarda lo necesario para pintar el menú y proteger rutas al recargar.
// El resto del perfil (documento, salario, cuentas...) vive solo en memoria y se pide con fetchMe().
const toStoredEmployee = (employee) => {
    if (!employee) return null;
    const roles = new Set(employee.roles || []);
    if (employee.isAdmin) roles.add("admin");
    return { _id: employee._id, name: employee.name, roles: [...roles] };
};

const storeEmployee = (employee) => {
    localStorage.setItem(AUTH_EMPLOYEE_KEY, JSON.stringify(toStoredEmployee(employee)));
};

const saveSession = (token, employee) => {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    storeEmployee(employee);
};

// Lee `exp` del JWT para no arrancar con una sesión vencida.
const isTokenExpired = (token) => {
    try {
        const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
        return typeof payload.exp === "number" && payload.exp * 1000 <= Date.now();
    } catch {
        return true;
    }
};

export const loginEmployee = createAsyncThunk(
    "auth/loginEmployee",
    async ({ email, password }, { rejectWithValue }) => {
        try {
            const { data, message } = await apiRequest("/auth/login", {
                method: "POST",
                body: { email, password },
                withMessage: true,
            });
            if (!data?.token) {
                return rejectWithValue("No se recibio el token de acceso");
            }
            return { token: data.token, employee: data.employee, message };
        } catch (error) {
            if (error?.details?.requiresPasswordSetup) {
                return rejectWithValue("Aun no tienes contrasena. Usa \"Cambiar contrasena\" para crearla con un codigo.");
            }
            return rejectWithValue(error.message || "No se pudo iniciar sesion");
        }
    }
);

export const requestPasswordCode = createAsyncThunk(
    "auth/requestPasswordCode",
    async ({ email }, { rejectWithValue }) => {
        try {
            const { message } = await apiRequest("/auth/password/code", {
                method: "POST",
                body: { email },
                withMessage: true,
            });
            return {
                message:
                    message ||
                    "Se envio un correo con el codigo de validacion. Este codigo estara habilitado durante 10 minutos.",
            };
        } catch (error) {
            return rejectWithValue(error.message || "No se pudo solicitar el codigo");
        }
    }
);

// Crear o cambiar la contraseña con el código; la API devuelve una sesión nueva.
const setPassword = async ({ email, verificationCode, newPassword }, { rejectWithValue }) => {
    try {
        const { data, message } = await apiRequest("/auth/password", {
            method: "POST",
            body: { email, verificationCode, newPassword },
            withMessage: true,
        });
        return { token: data?.token, employee: data?.employee, message: message || "Contrasena actualizada" };
    } catch (error) {
        return rejectWithValue(error.message || "No se pudo cambiar la contrasena");
    }
};

export const changePassword = createAsyncThunk("auth/changePassword", setPassword);
export const assignPassword = createAsyncThunk("auth/assignPassword", setPassword);

export const fetchMe = createAsyncThunk(
    "auth/fetchMe",
    withApi((_, { token }) => apiRequest("/employees/me", { token }))
);

export const updateEmployee = createAsyncThunk(
    "auth/updateEmployee",
    withApi(async (employeeData, { token }) => {
        const { data, message } = await apiRequest("/employees/me", {
            method: "PATCH",
            body: employeeData,
            token,
            withMessage: true,
        });
        return {
            message: message || "Perfil actualizado correctamente",
            updatedEmployee: data,
            submittedData: employeeData,
        };
    })
);

const startSession = (state, { token, employee }) => {
    if (!token) return;
    state.token = token;
    state.employee = employee || null;
    state.isAuthenticated = true;
    saveSession(token, employee);
};

const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
const initialToken = storedToken && !isTokenExpired(storedToken) ? storedToken : null;
const initialEmployee = initialToken ? toStoredEmployee(parseJsonSafe(localStorage.getItem(AUTH_EMPLOYEE_KEY))) : null;
if (initialToken) {
    // Reescribe sesiones antiguas que guardaban el perfil completo.
    storeEmployee(initialEmployee);
} else {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_EMPLOYEE_KEY);
}

const authSlice = createSlice({
    name: "auth",
    initialState: {
        employee: initialEmployee,
        token: initialToken,
        isAuthenticated: Boolean(initialToken),
        statusLogin: "idle",
        statusPasswordCode: "idle",
        statusChangePassword: "idle",
        statusAssignPassword: "idle",
        statusUpdateEmployee: "idle",
        errorUpdateEmployee: null,
        messageUpdateEmployee: null,
        message: null,
        error: null,
    },
    reducers: {
        clearAuthFeedback: (state) => {
            state.message = null;
            state.error = null;
        },
        clearUpdateEmployeeFeedback: (state) => {
            state.statusUpdateEmployee = "idle";
            state.errorUpdateEmployee = null;
            state.messageUpdateEmployee = null;
        },
        logOut: (state) => {
            state.employee = null;
            state.token = null;
            state.isAuthenticated = false;
            state.statusLogin = "idle";
            state.message = "Sesion cerrada";
            state.error = null;
            localStorage.removeItem(AUTH_TOKEN_KEY);
            localStorage.removeItem(AUTH_EMPLOYEE_KEY);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loginEmployee.pending, (state) => {
                state.statusLogin = "loading";
                state.error = null;
                state.message = null;
            })
            .addCase(loginEmployee.fulfilled, (state, action) => {
                state.statusLogin = "succeeded";
                startSession(state, action.payload);
                state.message = action.payload.message;
                state.error = null;
            })
            .addCase(loginEmployee.rejected, (state, action) => {
                state.statusLogin = "failed";
                state.error = action.payload || action.error.message || "Login failed";
                state.message = null;
                state.isAuthenticated = false;
            })
            .addCase(requestPasswordCode.pending, (state) => {
                state.statusPasswordCode = "loading";
                state.error = null;
                state.message = null;
            })
            .addCase(requestPasswordCode.fulfilled, (state, action) => {
                state.statusPasswordCode = "succeeded";
                state.message = action.payload.message;
                state.error = null;
            })
            .addCase(requestPasswordCode.rejected, (state, action) => {
                state.statusPasswordCode = "failed";
                state.error = action.payload || action.error.message || "Could not request code";
                state.message = null;
            })
            .addCase(changePassword.pending, (state) => {
                state.statusChangePassword = "loading";
                state.error = null;
                state.message = null;
            })
            .addCase(changePassword.fulfilled, (state, action) => {
                state.statusChangePassword = "succeeded";
                startSession(state, action.payload);
                state.message = action.payload.message;
                state.error = null;
            })
            .addCase(changePassword.rejected, (state, action) => {
                state.statusChangePassword = "failed";
                state.error = action.payload || action.error.message || "Could not change password";
                state.message = null;
            })
            .addCase(assignPassword.pending, (state) => {
                state.statusAssignPassword = "loading";
                state.error = null;
                state.message = null;
            })
            .addCase(assignPassword.fulfilled, (state, action) => {
                state.statusAssignPassword = "succeeded";
                startSession(state, action.payload);
                state.message = action.payload.message;
                state.error = null;
            })
            .addCase(assignPassword.rejected, (state, action) => {
                state.statusAssignPassword = "failed";
                state.error = action.payload || action.error.message || "Could not assign password";
                state.message = null;
            })
            .addCase(fetchMe.fulfilled, (state, action) => {
                if (!state.token || !action.payload) return;
                // /employees/me puede no traer los roles efectivos; se conservan los del login.
                state.employee = { ...state.employee, ...action.payload, roles: action.payload.roles || state.employee?.roles };
                storeEmployee(state.employee);
            })
            .addCase(updateEmployee.pending, (state) => {
                state.statusUpdateEmployee = "loading";
                state.errorUpdateEmployee = null;
                state.messageUpdateEmployee = null;
            })
            .addCase(updateEmployee.fulfilled, (state, action) => {
                state.statusUpdateEmployee = "succeeded";
                state.messageUpdateEmployee = action.payload.message;
                state.errorUpdateEmployee = null;
                const merged = { ...state.employee, ...(action.payload.updatedEmployee || action.payload.submittedData) };
                merged.roles = state.employee?.roles || merged.roles;
                state.employee = merged;
                storeEmployee(merged);
            })
            .addCase(updateEmployee.rejected, (state, action) => {
                state.statusUpdateEmployee = "failed";
                state.errorUpdateEmployee = action.payload || action.error.message || "Error al actualizar el perfil";
                state.messageUpdateEmployee = null;
            });
    },
});

export const { logOut, clearAuthFeedback, clearUpdateEmployeeFeedback } = authSlice.actions;
export default authSlice.reducer;
